// Import hooks.
import {
    useInputCurrency,
    UseInputCurrencyResults,
    useInputDate,
    UseInputDateResults,
    UseInputFormResult,
    useInputText,
    UseInputTextResults,
} from '@app/hooks/view/input';
// Import props.
import {
    FormProducerSignupProps,
} from '@app/props';

interface UseSubformProducerErrorsAndOmissionsParameters {
    id: string;
    showValidations?: boolean;
    props: FormProducerSignupProps;
}

interface UseSubformProducerErrorsAndOmissionsResults {
    aggregateLimitInput: UseInputCurrencyResults;
    carrierNameInput: UseInputTextResults;
    limitPerClaimInput: UseInputCurrencyResults;
    policyExpirationDateInput: UseInputDateResults;
    policyNumberInput: UseInputTextResults;
    checkFormValidationWithFocus: () => boolean;
    flagAllInputsToShowValidations: () => void;
}

const useSubformProducerErrorsAndOmissions = ( params: UseSubformProducerErrorsAndOmissionsParameters ): UseSubformProducerErrorsAndOmissionsResults => {
    
	// #region exported methods

    /**
     * Flags all inputs to show the validation messages.
     **/
    const flagAllInputsToShowValidations = (): void => {
        carrierNameInput.changeShowValidationFlag(true);
        policyNumberInput.changeShowValidationFlag(true);
        limitPerClaimInput.changeShowValidationFlag(true);
        aggregateLimitInput.changeShowValidationFlag(true);
        policyExpirationDateInput.changeShowValidationFlag(true);
    };

    /**
     * Method to check form validation with focus.
     **/
    const checkFormValidationWithFocus = (): boolean => {
        return (
			checkInputValidationWithFocus<string>(carrierNameInput)
            && checkInputValidationWithFocus<string>(policyNumberInput)
            && checkInputValidationWithFocus<Date | undefined>(policyExpirationDateInput)
            && checkInputValidationWithFocus<number | undefined>(limitPerClaimInput)
            && checkInputValidationWithFocus<number | undefined>(aggregateLimitInput)            
        );
    };

    // #endregion exported methods

    // #region private methods

    /**
     * Validates an individual input with focus.
     * @param input
     */
    const checkInputValidationWithFocus = <T>(input: UseInputFormResult<T>): boolean => {
        let isValid = input.isInputValid;
        if (!isValid) {
            input.focusToInput();
        }
        return isValid;
    };

    //#region id generators

    const generateIdForForm = (): string => {
        return params.id;
    };

    const generateIdForCarrierName = (): string => {
        return `${generateIdForForm()}-carrierName`;
    };

    const generateIdForPolicyNumberInput = (): string => {
        return `${generateIdForForm()}-policyNumber`;
    };

    const generateIdForPolicyExpirationDateInput = (): string => {
        return `${generateIdForForm()}-policyExpiration`;
    };

    const generateIdForLimitPerClaimInput = (): string => {
        return `${generateIdForForm()}-limitPerClaim`;
    };

    const generateIdForAggregateLimitInput = (): string => {
        return `${generateIdForForm()}-aggregateLimit`;
    };

    //#endregion id generators

    
    // #endregion private methods

    // #region variables

    const carrierNameInput: UseInputTextResults = useInputText({
        id: generateIdForCarrierName(),
        label: 'E&O Carrier Name',
        required: true,
        disabled: false,
        value: String.empty(),
        showValidation: params.showValidations
    });

    const policyNumberInput: UseInputTextResults = useInputText({
        id: generateIdForPolicyNumberInput(),
        label: 'Policy Number',
        required: true,
        disabled: false,
        value: String.empty(),
        showValidation: params.showValidations
    });

    const limitPerClaimInput: UseInputCurrencyResults = useInputCurrency({
        id: generateIdForLimitPerClaimInput(),
        label: 'Limit Per Claim',
        validationLabel: 'minimum E&O occurrence',
        required: true,
        disabled: false,
        minValue: 1000000,
        minValueInclusive: true,
        showValidation: params.showValidations
    });

    const aggregateLimitInput: UseInputCurrencyResults = useInputCurrency({
        id: generateIdForAggregateLimitInput(),
        label: 'Aggregate Limit',
        validationLabel: 'minimum aggregate limit',
        infoText: 'Leave blank if aggregate limit does not apply.',
        required: false,
        disabled: false,
        minValue: 1000000,
        minValueInclusive: true,
        showValidation: params.showValidations
    });
    
    const policyExpirationDateInput: UseInputDateResults = useInputDate({
        id: generateIdForPolicyExpirationDateInput(),
        label: 'Policy Expiration',
        validationLabel: 'policy expiration date', 
        infoText: 'Leave blank if expiration date does not apply.',
        required: false,
        disabled: false,
        value: undefined,
        minDate: new Date(),
        disableOutOfRangeDates: false,
        showValidation: params.showValidations
    });

    // #endregion variables

    return {
        carrierNameInput: carrierNameInput,
        policyNumberInput: policyNumberInput,
        limitPerClaimInput: limitPerClaimInput,
        aggregateLimitInput: aggregateLimitInput,
        policyExpirationDateInput: policyExpirationDateInput,
        flagAllInputsToShowValidations: flagAllInputsToShowValidations,
        checkFormValidationWithFocus: checkFormValidationWithFocus,
    };
};

// Export parameters.
export { UseSubformProducerErrorsAndOmissionsParameters };

// Export results.
export { UseSubformProducerErrorsAndOmissionsResults };

export default useSubformProducerErrorsAndOmissions;
